import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	faChevronDown,
	faChevronRight,
	faRightToBracket,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	SupportCompany,
	SupportOpenScrapingRequest,
	SupportRunningCampaign,
} from "../schema/support-dashboard";
import { useLoginAsAndNavigate } from "../hooks/useLoginAsAndNavigate";
import {
	formatActivityCount,
	formatActivityDay,
	getFeatureLabel,
} from "../utils/support-dashboard.utils";
import { DeliveryErrorsPanel } from "./DeliveryErrorsPanel";

interface ExpandedCompanyRowProps {
	company: SupportCompany;
	/** The last completed rollup day, so an empty company can say why it is empty. */
	lastRolledUpDate: string | null;
}

export const ExpandedCompanyRow: FunctionComponent<ExpandedCompanyRowProps> = ({
	company,
	lastRolledUpDate,
}) => {
	const { t } = useTranslation();
	const { loginAsAndNavigate, isLoading, error } = useLoginAsAndNavigate();
	const [statsExpanded, setStatsExpanded] = useState(false);

	const { features, month } = company.activityStats;
	const emptyText =
		lastRolledUpDate === null
			? t("supportDashboard.expanded.noRollupYet")
			: company.lastActivityDate === null
				? t("supportDashboard.expanded.noActivityRecorded")
				: month === null
					? t("supportDashboard.expanded.notFullyMeasured")
					: t("supportDashboard.expanded.noActivityInWindow");

	return (
		<div className="bg-brand-white px-6 py-4 border-t border-border-light">
			{company.hasSendErrors && <DeliveryErrorsPanel company={company} />}

			<div className="mt-4 flex flex-col gap-6 lg:flex-row">
				{/* Scraping Requests */}
				<div className="flex-1">
					<h4 className="text-sm font-semibold text-text mb-3">
						{t("supportDashboard.expanded.openScrapingRequests")} (
						{company.openScrapingRequests.length})
					</h4>
					{company.openScrapingRequests.length === 0 ? (
						<p className="text-sm text-text-meta">
							{t("supportDashboard.expanded.noOpenScrapingRequests")}
						</p>
					) : (
						<div className="flex flex-col gap-2">
							{company.openScrapingRequests.map((req: SupportOpenScrapingRequest) => (
								<div
									key={req.id}
									className="flex items-center justify-between rounded-lg border border-border-light bg-white px-4 py-2"
								>
									<span className="text-sm text-text">{req.name}</span>
									<span className="rounded-full bg-status-warning-light px-2.5 py-0.5 text-xs font-medium text-status-warning">
										{req.status}
									</span>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Running Campaigns */}
				<div className="flex-1">
					<h4 className="text-sm font-semibold text-text mb-3">
						{t("supportDashboard.expanded.runningCampaigns")} ({company.runningCampaigns.length})
					</h4>
					{error && <p className="text-sm text-status-error mb-2">{error}</p>}
					{company.runningCampaigns.length === 0 ? (
						<p className="text-sm text-text-meta">
							{t("supportDashboard.expanded.noRunningCampaigns")}
						</p>
					) : (
						<div className="flex flex-col gap-2">
							{company.runningCampaigns.map((campaign: SupportRunningCampaign) => (
								<div
									key={campaign.listId}
									className="flex items-center justify-between rounded-lg border border-border-light bg-white px-4 py-3"
								>
									<div className="flex flex-col gap-1">
										<span className="text-sm font-medium text-text">{campaign.listName}</span>
										<div className="flex gap-3 text-xs text-text-meta">
											<span>
												📧 {campaign.emailsSentToday} {t("supportDashboard.expanded.emails")}
											</span>
											<span>
												🔗 {campaign.linkedinInvitesToday} {t("supportDashboard.expanded.invites")}
											</span>
											<span>
												💬 {campaign.linkedinMessagesToday}{" "}
												{t("supportDashboard.expanded.messages")}
											</span>
										</div>
									</div>
									<Button
										color={ButtonColor.ACTION_SECONDARY}
										disabled={isLoading}
										image={<Icon className="mr-1.5" icon={faRightToBracket} />}
										isLoading={isLoading}
										size={ButtonSize.S}
										title={t("supportDashboard.expanded.loginAndOpen")}
										onClick={(e) => {
											e.stopPropagation();
											loginAsAndNavigate(company.id, campaign.listId);
										}}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Activity Stats — collapsible */}
			<div className="mt-4 border-t border-border-light pt-3">
				<button
					className="flex items-center gap-2 text-sm font-semibold text-text hover:text-gray-900 transition-colors"
					type="button"
					onClick={() => setStatsExpanded((v) => !v)}
				>
					<Icon
						className="text-text-meta"
						icon={statsExpanded ? faChevronDown : faChevronRight}
						size="sm"
					/>
					{t("supportDashboard.expanded.activityStats")}
				</button>

				{statsExpanded && (
					<div className="mt-3 overflow-x-auto">
						{features.length === 0 ? (
							<p className="text-sm text-text-meta">{emptyText}</p>
						) : (
							<table className="w-full text-left text-sm">
								<thead>
									<tr className="text-xs font-medium text-table-header">
										<th className="py-1.5 pr-6">{t("supportDashboard.expanded.feature")}</th>
										<th className="py-1.5 pr-6 text-right">
											{t("supportDashboard.expanded.day", {
												date: formatActivityDay(lastRolledUpDate),
											})}
										</th>
										<th className="py-1.5 pr-6 text-right">
											{t("supportDashboard.expanded.sevenDays")}
										</th>
										<th className="py-1.5 text-right">
											{t("supportDashboard.expanded.thirtyDays")}
										</th>
									</tr>
								</thead>
								<tbody>
									{features.map((feature) => (
										<tr key={feature.feature} className="border-t border-border-light">
											<td className="py-1.5 pr-6 text-text">{getFeatureLabel(feature.feature)}</td>
											<td className="py-1.5 pr-6 text-right text-text-meta">
												{formatActivityCount(feature.day)}
											</td>
											<td className="py-1.5 pr-6 text-right text-text-meta">
												{formatActivityCount(feature.week)}
											</td>
											<td className="py-1.5 text-right text-text-meta">
												{formatActivityCount(feature.month)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
